import { useState } from "react";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Modal } from "../../Modal";
import { Text } from "../../Text";
import { FieldLabel, ModalContent, ModalFooter } from "../shared";

export type OAuthAppValues = {
  name: string;
  redirectUris: string[];
};

export function OAuthAppModal({
  initial,
  onClose,
  onSubmit,
}: {
  initial?: OAuthAppValues;
  onClose: () => void;
  onSubmit: (values: OAuthAppValues) => void;
}) {
  const editing = initial !== undefined;
  const [name, setName] = useState(initial?.name ?? "");
  // The consent flow only redirects to a registered URI, so this is the app's
  // allow-list. Separate multiple URIs with a space or comma.
  const [redirectUris, setRedirectUris] = useState(
    (initial?.redirectUris ?? []).join(" ")
  );

  const parsedUris = redirectUris
    .split(/[\s,]+/)
    .map((u) => u.trim())
    .filter((u) => u.length > 0);

  const canSubmit = name.trim().length > 0 && parsedUris.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), redirectUris: parsedUris });
    onClose();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalContent>
        <Text variant="text-regular-md">
          {editing ? "Edit OAuth app" : "Register OAuth app"}
        </Text>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Name</Text>
          </Box>
          <Input
            autoFocus
            placeholder="e.g. Tines"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
        </FieldLabel>

        <FieldLabel>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Redirect URIs</Text>
          </Box>
          <Input
            placeholder="https://app.example.com/oauth/callback"
            value={redirectUris}
            onChange={(e) => setRedirectUris(e.target.value)}
            style={{ minWidth: 0, width: "100%" }}
          />
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              HTTPS URLs, separated by spaces or commas. The consent flow will
              only redirect to a registered URI.
            </Text>
          </Box>
        </FieldLabel>

        <div style={{ height: 8 }} />
        <ModalFooter>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={handleSubmit} disabled={!canSubmit}>
            {editing ? "Save" : "Register"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
